"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStore = exports.sendHeartbeat = exports.getDeviceInfo = exports.API_BASE_URL = exports.getApiBaseUrl = void 0;
const zustand_1 = require("zustand");
const VoiceService_1 = require("../services/VoiceService");
const AudioService_1 = require("../services/AudioService");
const RealtimeService_1 = require("../services/RealtimeService");
const TabSyncService_1 = require("../services/TabSyncService");
const realtimeConfig_1 = require("../config/realtimeConfig");
const getApiBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        const origin = window.location.origin;
        if (origin.startsWith('http')) {
            return `${origin}/api/v1`;
        }
    }
    return 'https://meci.teymensel.com/api/v1';
};
exports.getApiBaseUrl = getApiBaseUrl;
exports.API_BASE_URL = (0, exports.getApiBaseUrl)();
const getImageUrl = (urlOrPath, type) => {
    if (!urlOrPath)
        return urlOrPath;
    if (urlOrPath.startsWith('http') || urlOrPath.startsWith('data:'))
        return urlOrPath;
    const baseUploads = 'https://meci.teymensel.com/uploads/profile';
    return type === 'avatar' ? `${baseUploads}/pp/${urlOrPath}` : `${baseUploads}/banner/${urlOrPath}`;
};
const mapUser = (user) => {
    if (!user)
        return user;
    return {
        ...user,
        id: String(user.id),
        avatar: getImageUrl(user.avatar, 'avatar'),
        banner: getImageUrl(user.banner, 'banner'),
        displayName: user.display_name || user.displayName || user.username,
        bannerColor: user.banner_color || user.bannerColor,
        createdAt: user.created_at ? new Date(user.created_at) : (user.createdAt ? new Date(user.createdAt) : undefined),
    };
};
const getDeviceInfo = async (loginMethod = null, context) => {
    let staticInfo = {
        app_version: '1.1.0',
        os_name: 'Web Browser',
        os_version: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
        os_build: '',
        device_model: 'Browser Client',
        device_name: 'Browser Client',
        hwid: 'HWID_BROWSER',
        cpu: 'Unknown CPU',
        gpu: 'Unknown GPU',
        ram: 'Unknown RAM',
        serial_number: 'N/A'
    };
    try {
        if (typeof window !== 'undefined' && window.require) {
            const electron = window.require('electron');
            if (electron && electron.ipcRenderer) {
                const electronInfo = await electron.ipcRenderer.invoke('get-device-info');
                staticInfo = { ...staticInfo, ...electronInfo };
            }
        }
    }
    catch (e) {
        console.warn('Electron device info skipped in browser context:', e);
    }
    // Calculate dynamic properties
    let screenResolution = 'Unknown';
    if (typeof window !== 'undefined' && window.screen) {
        screenResolution = `${window.screen.width}x${window.screen.height}`;
    }
    let locale = 'tr_TR';
    if (typeof navigator !== 'undefined') {
        locale = navigator.language.replace('-', '_') || 'tr_TR';
    }
    let timezone = 'GMT+03:00';
    if (typeof Intl !== 'undefined') {
        const offset = new Date().getTimezoneOffset();
        const absOffset = Math.abs(offset);
        const hours = Math.floor(absOffset / 60);
        const minutes = absOffset % 60;
        const sign = offset <= 0 ? '+' : '-';
        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const zoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
        timezone = `GMT${sign}${formattedHours}:${formattedMinutes} (${zoneName})`;
    }
    const notificationsEnabled = typeof Notification !== 'undefined' ? (Notification.permission === 'granted' ? 1 : 0) : 0;
    let localTime = new Date().toLocaleString('tr-TR');
    if (typeof Intl !== 'undefined') {
        try {
            const zoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (zoneName) {
                localTime = new Date().toLocaleString('tr-TR', { timeZone: zoneName });
            }
        }
        catch { /* fallback to default locale string */ }
    }
    const theme = typeof document !== 'undefined' ? (document.documentElement.getAttribute('data-theme') || 'cyberpunk') : 'cyberpunk';
    // Visibility / focus detection
    const isBackground = context?.is_background ?? (typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false);
    return {
        app_version: staticInfo.app_version || '1.1.0',
        os_name: staticInfo.os_name,
        os_version: staticInfo.os_version,
        os_build: staticInfo.os_build,
        device_model: staticInfo.device_model,
        device_name: staticInfo.device_name,
        hwid: staticInfo.hwid,
        cpu: staticInfo.cpu,
        gpu: staticInfo.gpu,
        ram: staticInfo.ram,
        serial_number: staticInfo.serial_number,
        screen_resolution: screenResolution,
        locale: locale,
        timezone: timezone,
        login_method: loginMethod || 'session',
        notifications_enabled: notificationsEnabled,
        local_time: localTime,
        theme: theme,
        // Activity context
        active_screen: context?.active_screen || 'unknown',
        active_channel_id: context?.active_channel_id || null,
        active_server_id: context?.active_server_id || null,
        is_background: isBackground ? 1 : 0,
    };
};
exports.getDeviceInfo = getDeviceInfo;
/**
 * Sends an activity heartbeat to the server.
 * Includes current screen, channel, server context and background state.
 * Fire-and-forget: errors are suppressed.
 */
const sendHeartbeat = async (context) => {
    try {
        const deviceInfo = await (0, exports.getDeviceInfo)(context.login_method || 'Heartbeat', context);
        const params = {};
        Object.entries(deviceInfo).forEach(([key, val]) => {
            if (val !== null && val !== undefined)
                params[key] = String(val);
        });
        const queryParams = new URLSearchParams(params).toString();
        await fetch(`${exports.API_BASE_URL}/auth/me?${queryParams}`, {
            credentials: 'include',
        });
    }
    catch {
        // Fire-and-forget: ignore errors
    }
};
exports.sendHeartbeat = sendHeartbeat;
const randomAccentColor = () => {
    const colors = ['#b48c5c', '#18181b', '#0f1215', '#1f1f23', '#f5eadf', '#26272c'];
    return colors[Math.floor(Math.random() * colors.length)];
};
const resolveUserInfo = (state, uid) => {
    const uidStr = String(uid);
    const isCurrentUser = String(state.currentUser?.id) === uidStr;
    if (isCurrentUser && state.currentUser) {
        return {
            displayName: state.currentUser.displayName || state.currentUser.username || 'Siz',
            avatar: state.currentUser.avatar,
        };
    }
    // 1. Check in state.users
    const user = state.users?.find((u) => String(u.id) === uidStr);
    if (user && (user.displayName || user.display_name || user.username)) {
        return {
            displayName: user.displayName || user.display_name || user.username,
            avatar: user.avatar,
        };
    }
    // 2. Check in state.currentServerMembers
    const member = state.currentServerMembers?.find((m) => String(m.id) === uidStr || String(m.user_id) === uidStr || String(m.userId) === uidStr);
    if (member && (member.displayName || member.display_name || member.nickname || member.username)) {
        return {
            displayName: member.displayName || member.display_name || member.nickname || member.username,
            avatar: member.avatar,
        };
    }
    // 3. Check in state.friends
    const friend = state.friends?.find((f) => String(f.id) === uidStr);
    if (friend && (friend.displayName || friend.display_name || friend.username)) {
        return {
            displayName: friend.displayName || friend.display_name || friend.username,
            avatar: friend.avatar,
        };
    }
    // 4. Check in serverVoiceStates
    for (const channelUsers of Object.values(state.serverVoiceStates || {})) {
        if (Array.isArray(channelUsers)) {
            const su = channelUsers.find((x) => String(x.userId || x.user_id || x.id) === uidStr);
            if (su && (su.displayName || su.username)) {
                return {
                    displayName: su.displayName || su.username,
                    avatar: su.avatar,
                };
            }
        }
    }
    // 5. Check in messages author
    const msg = state.messages?.find((m) => String(m.authorId) === uidStr);
    if (msg && msg.authorName) {
        return {
            displayName: msg.authorName,
            avatar: msg.authorAvatar,
        };
    }
    return {
        displayName: `Kullanıcı ${uid}`,
        avatar: undefined,
    };
};
const buildParticipant = (state, uid, overrides = {}) => {
    const uidStr = String(uid);
    const isLocalUser = !!(overrides.isLocal || (state.currentUser && String(state.currentUser.id) === uidStr));
    let displayName = `Kullanıcı ${uid}`;
    let avatar = undefined;
    if (isLocalUser && state.currentUser) {
        displayName = state.currentUser.displayName || state.currentUser.username || 'Admin';
        avatar = state.currentUser.avatar;
    }
    else {
        // If overrides provided a real display name, prefer it
        if (overrides.displayName && !overrides.displayName.startsWith('Kullanıcı ') && !overrides.displayName.startsWith('User #')) {
            displayName = overrides.displayName;
        }
        else {
            const userInfo = resolveUserInfo(state, uid);
            displayName = userInfo.displayName;
            avatar = userInfo.avatar;
        }
    }
    const effectiveUserId = isLocalUser && state.currentUser ? String(state.currentUser.id) : uidStr;
    const isBotUser = Boolean(overrides.isBot ||
        uidStr === '370846327506079745' ||
        uidStr === '100' ||
        (displayName && displayName.toLowerCase().includes('bot')) ||
        state.users?.some((u) => String(u.id) === uidStr && (Boolean(u.is_bot) || Boolean(u.isBot) || u.role === 'bot')));
    const base = {
        id: isLocalUser && state.currentUser ? `voice-${state.currentUser.id}` : `voice-${uid}`,
        userId: effectiveUserId,
        displayName,
        avatar,
        accentColor: randomAccentColor(),
        muted: false,
        deafened: false,
        cameraEnabled: false,
        screenSharing: false,
        speaking: false,
        badge: isLocalUser ? 'Siz' : (isBotUser ? 'BOT' : undefined),
        isLocal: isLocalUser,
        isBot: isBotUser,
    };
    return { ...base, ...overrides, isLocal: isLocalUser, isBot: isBotUser || Boolean(overrides.isBot) };
};
exports.useStore = (0, zustand_1.create)((set, get) => {
    const savedPin = localStorage.getItem('meci_app_pin');
    return {
        currentUser: null,
        appPin: savedPin,
        isLocked: !!savedPin,
        isVersionBlocked: false,
        incomingCall: null,
        setIncomingCall: (incomingCall) => set({ incomingCall }),
        sendHeartbeat: async (context) => {
            await (0, exports.sendHeartbeat)(context);
        },
        // Invite state
        pendingInviteCode: null,
        setPendingInviteCode: (code) => set({ pendingInviteCode: code }),
        // Server Invite & Share Modal
        inviteShareModalOpen: false,
        inviteShareServerId: null,
        openInviteShareModal: (serverId) => {
            const targetServerId = serverId || get().selectedServerId;
            set({ inviteShareModalOpen: true, inviteShareServerId: targetServerId });
        },
        closeInviteShareModal: () => set({ inviteShareModalOpen: false, inviteShareServerId: null }),
        joinByInvite: async (inviteCode) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/join`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ inviteCode }),
                    credentials: 'include',
                });
                const data = await response.json();
                if (response.ok) {
                    await get().fetchServers();
                    // Select the joined server
                    if (data.server_id) {
                        set({ selectedServerId: data.server_id, discoveryOpen: false, selectedDMUserId: null });
                    }
                    return data;
                }
                else {
                    throw new Error(data.error || 'Davet bağlantısı geçersiz veya süresi dolmuş.');
                }
            }
            catch (error) {
                throw error;
            }
        },
        setVersionBlocked: (blocked) => set({ isVersionBlocked: blocked }),
        setAppPin: (pin) => {
            if (pin) {
                localStorage.setItem('meci_app_pin', pin);
                set({ appPin: pin });
            }
            else {
                localStorage.removeItem('meci_app_pin');
                set({ appPin: null, isLocked: false });
            }
        },
        lockApp: () => {
            if (get().appPin) {
                set({ isLocked: true });
            }
        },
        unlockApp: (pin) => {
            if (get().appPin === pin) {
                set({ isLocked: false });
                return true;
            }
            return false;
        },
        servers: [],
        categories: [
            { id: 'cat1', name: 'TEXT CHANNELS', serverId: 's1', collapsed: false },
            { id: 'cat2', name: 'VOICE CHANNELS', serverId: 's1', collapsed: false },
            { id: 'cat3', name: 'INFORMATION', serverId: 's1', collapsed: false },
        ],
        channels: [],
        messages: [
            {
                id: 'm1',
                content: 'Welcome to Meci! 🎉',
                authorId: '1',
                channelId: 'c1',
                timestamp: new Date(Date.now() - 3600000),
                isPinned: true,
                reactions: [
                    { emoji: '👋', count: 3, userIds: ['2', '3', '4'] },
                    { emoji: '🎉', count: 2, userIds: ['2', '5'] },
                ],
            },
            {
                id: 'm2',
                content: 'This is a professional Discord-like application built with Electron, React, and TypeScript.',
                authorId: '2',
                channelId: 'c1',
                timestamp: new Date(Date.now() - 3000000),
            },
            {
                id: 'm3',
                content: 'Looking great! The UI is super clean 🔥',
                authorId: '3',
                channelId: 'c1',
                timestamp: new Date(Date.now() - 1800000),
            },
            {
                id: 'm4',
                content: 'Can\'t wait to see more features!',
                authorId: '4',
                channelId: 'c1',
                timestamp: new Date(Date.now() - 900000),
            },
        ],
        selectedServerId: localStorage.getItem('meci_last_server') || null,
        selectedChannelId: localStorage.getItem('meci_last_channel') || null,
        users: [],
        setCurrentUser: (user) => set({ currentUser: user }),
        mobileSidebarOpen: false,
        setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
        toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
        setSelectedServer: (serverId) => {
            if (serverId) {
                localStorage.setItem('meci_last_server', serverId);
                get().fetchServerMembers(serverId);
                get().fetchVoiceStates(serverId);
            }
            set({ selectedServerId: serverId, activeReply: null, selectedDMUserId: null, voiceViewVisible: false });
        },
        setSelectedChannel: (channelId) => {
            if (channelId) {
                localStorage.setItem('meci_last_channel', channelId);
                RealtimeService_1.realtimeService.joinChatChannel(channelId);
            }
            // Also clear selectedDMUserId so switching from DM back to a channel works correctly, and close mobile menu
            set({ selectedChannelId: channelId, activeReply: null, selectedDMUserId: null, mobileSidebarOpen: false, voiceViewVisible: false });
        },
        addMessage: (message) => set((state) => ({
            messages: [
                ...state.messages,
                {
                    ...message,
                    timestamp: new Date(message.timestamp),
                    parentId: message.parentId ?? state.activeReply ?? undefined,
                },
            ],
            activeReply: null,
        })),
        getChannelMessages: (channelId) => {
            return get()
                .messages
                .filter(msg => msg.channelId === channelId)
                .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        },
        getChannelById: (channelId) => {
            return get().channels.find(channel => channel.id === channelId);
        },
        getUserById: (userId) => {
            return get().users.find(user => user.id === userId);
        },
        getMessageById: (messageId) => {
            // Check both channel messages and DM messages so replies work in DM too
            return get().messages.find(message => message.id === messageId)
                || get().directMessages.find(message => message.id === messageId);
        },
        toggleCategory: (categoryId) => set((state) => ({
            categories: state.categories.map(cat => cat.id === categoryId ? { ...cat, collapsed: !cat.collapsed } : cat),
        })),
        typingUsers: {},
        typingUsernames: {},
        userPanelOpen: false,
        membersPanelOpen: true,
        selectedUserId: null,
        activeReply: null,
        settingsOpen: false,
        serverSettingsOpen: false,
        discoveryOpen: false,
        searchQuery: '',
        searchFilters: {},
        voiceConnection: {
            status: 'disconnected',
            channelId: null,
            muted: false,
            deafened: false,
            cameraEnabled: false,
            screenSharing: false,
            noiseSuppression: (0, realtimeConfig_1.getNoiseSuppressionEnabled)(),
            noiseEngine: (0, realtimeConfig_1.getNoiseEngine)(),
            connectedAt: null,
            error: null,
        },
        voiceParticipants: [],
        voiceViewChannelId: null,
        voiceViewVisible: false,
        voiceStats: undefined,
        addTypingUser: (channelId, userId, username) => set((state) => ({
            typingUsers: {
                ...state.typingUsers,
                [channelId]: [...(state.typingUsers[channelId] || []), userId].filter((v, i, a) => a.indexOf(v) === i),
            },
            typingUsernames: username ? {
                ...state.typingUsernames,
                [userId]: username,
            } : state.typingUsernames,
        })),
        removeTypingUser: (channelId, userId) => set((state) => ({
            typingUsers: {
                ...state.typingUsers,
                [channelId]: (state.typingUsers[channelId] || []).filter(id => id !== userId),
            },
        })),
        toggleUserPanel: () => set((state) => ({ userPanelOpen: !state.userPanelOpen })),
        toggleMembersPanel: () => set((state) => ({ membersPanelOpen: !state.membersPanelOpen })),
        setSelectedUser: (userId) => set({ selectedUserId: userId, userPanelOpen: userId !== null }),
        addReaction: (messageId, emoji, userId) => set((state) => ({
            messages: state.messages.map(msg => {
                if (msg.id !== messageId)
                    return msg;
                const reactions = msg.reactions || [];
                const existingReaction = reactions.find(r => r.emoji === emoji);
                if (existingReaction) {
                    const hasReacted = existingReaction.userIds.includes(userId);
                    return {
                        ...msg,
                        reactions: reactions.map(r => r.emoji === emoji
                            ? {
                                ...r,
                                userIds: hasReacted
                                    ? r.userIds.filter(id => id !== userId)
                                    : [...r.userIds, userId],
                                count: hasReacted ? r.count - 1 : r.count + 1,
                            }
                            : r).filter(r => r.count > 0),
                    };
                }
                return {
                    ...msg,
                    reactions: [...reactions, { emoji, count: 1, userIds: [userId] }],
                };
            }),
        })),
        setActiveReply: (messageId, channelId) => {
            // Check both channel messages and direct messages so reply works in DM mode too
            const messageExists = get().messages.some(message => message.id === messageId)
                || get().directMessages.some(message => message.id === messageId);
            if (!messageExists)
                return;
            set({ activeReply: messageId });
        },
        clearActiveReply: () => set({ activeReply: null }),
        deleteMessage: async (messageId, isDM) => {
            try {
                // Optimistically remove from state immediately (text & attachments disappear completely)
                set((state) => ({
                    messages: state.messages.filter(m => String(m.id) !== String(messageId)),
                    directMessages: state.directMessages.filter(m => String(m.id) !== String(messageId)),
                }));
                RealtimeService_1.realtimeService.sendDelete({ channelId: get().selectedChannelId || '', messageId });
                const endpoint = isDM ? `${exports.API_BASE_URL}/dms/delete` : `${exports.API_BASE_URL}/messages/delete`;
                await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messageId }),
                    credentials: 'include',
                });
            }
            catch (err) {
                console.warn('deleteMessage error:', err);
            }
        },
        editMessage: async (messageId, content, isDM) => {
            try {
                RealtimeService_1.realtimeService.sendEdit({ channelId: get().selectedChannelId || '', messageId, content });
                const endpoint = isDM ? `${exports.API_BASE_URL}/dms/update` : `${exports.API_BASE_URL}/messages/update`;
                await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messageId, content }),
                    credentials: 'include',
                });
                set((state) => ({
                    messages: state.messages.map(m => m.id === messageId ? { ...m, content, edited: true } : m),
                    directMessages: state.directMessages.map(m => String(m.id) === String(messageId) ? { ...m, content, edited: true } : m),
                }));
            }
            catch (err) {
                console.warn('editMessage error:', err);
            }
        },
        fetchMessageEdits: async (messageId, isDM) => {
            try {
                const res = await fetch(`${exports.API_BASE_URL}/messages/edits?messageId=${encodeURIComponent(messageId)}&isDM=${isDM ? 1 : 0}`, {
                    credentials: 'include',
                });
                if (res.ok) {
                    return await res.json();
                }
            }
            catch (err) {
                console.warn('fetchMessageEdits error:', err);
            }
            return [];
        },
        setSettingsOpen: (open) => set({ settingsOpen: open }),
        setServerSettingsOpen: (open) => set({ serverSettingsOpen: open }),
        setDiscoveryOpen: (open) => set({ discoveryOpen: open, selectedServerId: open ? null : get().selectedServerId, voiceViewVisible: false }),
        setSearchQuery: (query) => set({ searchQuery: query }),
        setSearchFilters: (filters) => set({ searchFilters: filters }),
        uploadFile: async (file, type) => {
            const formData = new FormData();
            formData.append('file', file);
            const url = new URL(`${exports.API_BASE_URL}/upload.php`);
            if (type)
                url.searchParams.append('type', type);
            const response = await fetch(url.toString(), {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok)
                throw new Error(data.error || 'Yükleme başarısız');
            return data;
        },
        joinVoiceChannel: async (channelId) => {
            const state = get();
            const current = state.voiceConnection;
            // Eğer zaten bu kanalda bağlanıyorsak veya bağlıysak, sadece görünümü aç
            if (current.channelId === channelId && current.status !== 'disconnected') {
                set({ voiceViewChannelId: channelId, voiceViewVisible: true });
                return;
            }
            // Başka bir kanala bağlanma sürecindeyken tekrar join çağrısı gelirse, yeni join başlatma
            if (current.status === 'connecting' || current.status === 'connected') {
                set({ voiceViewVisible: true });
                return;
            }
            const rawUid = Number(state.currentUser?.id);
            // Agora Number UID range: use direct user ID for standard users to match database
            const uid = (Number.isInteger(rawUid) && rawUid > 0)
                ? (rawUid < 100000 ? rawUid : ((rawUid % 9999) + 1))
                : (Math.floor(Math.random() * 9998) + 1);
            if (typeof window !== 'undefined') {
                window.__meci_current_user_id = String(state.currentUser?.id || uid);
            }
            set({
                voiceConnection: { ...current, channelId, status: 'connecting' },
                voiceViewChannelId: channelId,
                voiceViewVisible: true,
            });
            try {
                VoiceService_1.voiceService.removeAllListeners();
                VoiceService_1.voiceService.on('local-tracks', () => {
                    set((prev) => ({
                        voiceParticipants: [
                            buildParticipant(prev, uid, {
                                muted: prev.voiceConnection.muted,
                                cameraEnabled: prev.voiceConnection.cameraEnabled,
                                screenSharing: prev.voiceConnection.screenSharing,
                                isLocal: true,
                            }),
                            ...prev.voiceParticipants.filter((p) => !p.isLocal),
                        ],
                    }));
                });
                VoiceService_1.voiceService.on('user-joined', ({ user }) => {
                    const currentUserId = String(get().currentUser?.id);
                    const userUidStr = String(user.uid);
                    if (userUidStr === currentUserId || userUidStr === String(uid)) {
                        return;
                    }
                    AudioService_1.audioService.playJoinVoice();
                    set((prev) => {
                        const exists = prev.voiceParticipants.some((p) => p.userId === userUidStr || p.id === `voice-${userUidStr}` || (p.isLocal && userUidStr === currentUserId));
                        if (exists) {
                            return {
                                voiceParticipants: prev.voiceParticipants.map((p) => p.userId === userUidStr
                                    ? {
                                        ...p,
                                        screenSharing: user.screenSharing ?? p.screenSharing,
                                        cameraEnabled: user.cameraEnabled ?? p.cameraEnabled,
                                        muted: user.muted ?? p.muted,
                                        deafened: user.deafened ?? p.deafened,
                                    }
                                    : p),
                            };
                        }
                        return {
                            voiceParticipants: [
                                ...prev.voiceParticipants,
                                buildParticipant(prev, user.uid, {
                                    screenSharing: Boolean(user.screenSharing),
                                    cameraEnabled: Boolean(user.cameraEnabled),
                                    muted: Boolean(user.muted),
                                    deafened: Boolean(user.deafened),
                                }),
                            ],
                        };
                    });
                });
                VoiceService_1.voiceService.on('user-published', ({ user, mediaType, videoTrack }) => {
                    if (mediaType === 'video' && videoTrack) {
                        const uidStr = String(user.uid);
                        // Check live snapshot to see if this user is screen sharing or camera
                        let isScreen = Boolean(user.screenSharing);
                        let isCamera = Boolean(user.cameraEnabled);
                        let peerName;
                        try {
                            const liveSnapshot = VoiceService_1.voiceService.getLiveSnapshot();
                            const peerSnapshot = liveSnapshot?.peers?.find((p) => String(p.userId) === uidStr);
                            if (peerSnapshot) {
                                if (peerSnapshot.screen)
                                    isScreen = true;
                                if (peerSnapshot.camera)
                                    isCamera = true;
                                if (peerSnapshot.displayName)
                                    peerName = peerSnapshot.displayName;
                            }
                        }
                        catch (e) { }
                        // Default to screen share if not specified (remote video is overwhelmingly screen shares in Meci)
                        if (!isScreen && !isCamera) {
                            isScreen = true;
                        }
                        // Ensure participant exists and mark active video/screen
                        set((prev) => {
                            const exists = prev.voiceParticipants.some((p) => p.userId === uidStr);
                            const voiceParticipants = exists
                                ? prev.voiceParticipants.map((p) => p.userId === uidStr
                                    ? {
                                        ...p,
                                        displayName: peerName || p.displayName,
                                        screenSharing: isScreen ? true : p.screenSharing,
                                        cameraEnabled: isCamera ? true : p.cameraEnabled,
                                    }
                                    : p)
                                : [
                                    ...prev.voiceParticipants,
                                    buildParticipant(prev, user.uid, {
                                        displayName: peerName,
                                        screenSharing: isScreen,
                                        cameraEnabled: isCamera,
                                    }),
                                ];
                            return { voiceParticipants };
                        });
                        const tryPlay = (attempt = 0) => {
                            // Hero container öncelikli, yoksa kart container
                            const heroContainer = document.getElementById(`video-hero-${user.uid}`);
                            const cardContainer = document.getElementById(`video-card-${user.uid}`);
                            const container = heroContainer || cardContainer;
                            if (container) {
                                console.log(`[Voice] Playing video for ${user.uid} in ${heroContainer ? 'hero' : 'card'} container`);
                                videoTrack.play(container);
                            }
                            else if (attempt < 20) {
                                setTimeout(() => tryPlay(attempt + 1), 100);
                            }
                        };
                        tryPlay();
                    }
                });
                VoiceService_1.voiceService.on('peer-state-change', ({ userId, muted, deafened, cameraEnabled, screenSharing }) => {
                    set((prev) => {
                        const uidStr = String(userId);
                        const exists = prev.voiceParticipants.some((p) => p.userId === uidStr);
                        if (exists) {
                            return {
                                voiceParticipants: prev.voiceParticipants.map((p) => {
                                    if (p.userId === uidStr) {
                                        return {
                                            ...p,
                                            ...(typeof muted === 'boolean' ? { muted } : {}),
                                            ...(typeof deafened === 'boolean' ? { deafened } : {}),
                                            ...(typeof cameraEnabled === 'boolean' ? { cameraEnabled } : {}),
                                            ...(typeof screenSharing === 'boolean' ? { screenSharing } : {}),
                                        };
                                    }
                                    return p;
                                }),
                            };
                        }
                        else {
                            // Participant is in WebRTC but missing from store! Add them immediately!
                            let displayName;
                            try {
                                const liveSnapshot = VoiceService_1.voiceService.getLiveSnapshot();
                                displayName = liveSnapshot?.peers?.find((p) => String(p.userId) === uidStr)?.displayName;
                            }
                            catch (e) { }
                            return {
                                voiceParticipants: [
                                    ...prev.voiceParticipants,
                                    buildParticipant(prev, userId, {
                                        displayName,
                                        muted: typeof muted === 'boolean' ? muted : false,
                                        deafened: typeof deafened === 'boolean' ? deafened : false,
                                        cameraEnabled: typeof cameraEnabled === 'boolean' ? cameraEnabled : false,
                                        screenSharing: typeof screenSharing === 'boolean' ? screenSharing : false,
                                    }),
                                ],
                            };
                        }
                    });
                });
                VoiceService_1.voiceService.on('user-unpublished', ({ uid: remoteUid, mediaType }) => {
                    if (mediaType === 'video') {
                        set((prev) => ({
                            voiceParticipants: prev.voiceParticipants.map((p) => p.userId === String(remoteUid) ? { ...p, cameraEnabled: false, screenSharing: false } : p),
                        }));
                    }
                });
                VoiceService_1.voiceService.on('user-left', ({ uid: remoteUid }) => {
                    AudioService_1.audioService.playLeaveVoice();
                    set((prev) => ({
                        voiceParticipants: prev.voiceParticipants.filter((p) => p.userId !== String(remoteUid)),
                    }));
                });
                VoiceService_1.voiceService.on('volume-indicator', ({ uid: speakingUid, level }) => {
                    set((prev) => ({
                        voiceParticipants: prev.voiceParticipants.map((p) => p.userId === String(speakingUid) ? { ...p, speaking: level > 3 } : p),
                    }));
                });
                VoiceService_1.voiceService.on('stats', (stats) => {
                    set((prev) => ({
                        voiceStats: {
                            ...(prev.voiceStats || {}),
                            ...stats,
                        },
                    }));
                });
                const currentUser = get().currentUser;
                await VoiceService_1.voiceService.joinChannel(channelId, uid, {
                    displayName: currentUser?.displayName || currentUser?.username,
                    avatar: currentUser?.avatar,
                });
                AudioService_1.audioService.playJoinVoice();
                const ch = get().getChannelById(channelId);
                if (ch && ch.serverId) {
                    const currentUser = get().currentUser;
                    fetch(`${exports.API_BASE_URL}/voice/state/join`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            channelId,
                            serverId: ch.serverId,
                            userId: currentUser?.id, // fallback if PHP session is unavailable
                        }),
                        credentials: 'include',
                    }).then(() => get().fetchVoiceStates(ch.serverId)).catch(console.warn);
                }
                set((prev) => ({
                    voiceConnection: {
                        ...prev.voiceConnection,
                        status: 'connected',
                        connectedAt: Date.now(),
                    },
                    voiceViewVisible: true,
                    voiceParticipants: prev.voiceParticipants.length
                        ? prev.voiceParticipants
                        : [buildParticipant(prev, uid, { muted: prev.voiceConnection.muted, isLocal: true })],
                }));
            }
            catch (error) {
                console.error('Failed to join Agora channel', error);
                const errorMessage = error?.message || 'Bağlantı hatası oluştu.';
                set((prev) => ({
                    voiceConnection: {
                        ...prev.voiceConnection,
                        status: 'disconnected',
                        error: errorMessage,
                    },
                }));
                const message = typeof error?.message === 'string' ? error.message : '';
                const isAlreadyConnecting = message.includes('Client already in connecting/connected state');
                // Eğer hata sadece zaten bağlanıyor/bağlı olduğumuza dair bir uyarı ise, state'i bozma
                if (!isAlreadyConnecting) {
                    set((prev) => ({
                        voiceConnection: {
                            ...prev.voiceConnection,
                            status: 'disconnected',
                            channelId: null,
                            connectedAt: null,
                        },
                        voiceParticipants: [],
                    }));
                }
            }
        },
        leaveVoiceChannel: () => {
            VoiceService_1.voiceService.leaveChannel().catch((err) => console.error('leaveChannel failed', err));
            AudioService_1.audioService.playLeaveVoice();
            const currentServerId = get().selectedServerId;
            const currentUserId = get().currentUser?.id;
            fetch(`${exports.API_BASE_URL}/voice/state/leave`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: currentUserId }),
                credentials: 'include',
            }).then(() => {
                if (currentServerId)
                    get().fetchVoiceStates(currentServerId);
            }).catch(console.warn);
            set((state) => ({
                voiceConnection: {
                    ...state.voiceConnection,
                    channelId: null,
                    status: 'disconnected',
                    connectedAt: null,
                    cameraEnabled: false,
                    screenSharing: false,
                },
                voiceParticipants: [],
                voiceViewChannelId: null,
                voiceViewVisible: false,
                voiceStats: undefined,
            }));
        },
        toggleVoiceMute: () => set((state) => {
            const nextMuted = !state.voiceConnection.muted;
            VoiceService_1.voiceService.toggleMute(nextMuted).catch(console.error);
            TabSyncService_1.tabSyncService.broadcastMute(nextMuted);
            if (nextMuted) {
                AudioService_1.audioService.playMute();
            }
            else {
                AudioService_1.audioService.playUnmute();
            }
            return {
                voiceConnection: { ...state.voiceConnection, muted: nextMuted },
                voiceParticipants: state.voiceParticipants.map((p) => p.isLocal ? { ...p, muted: nextMuted } : p),
            };
        }),
        toggleVoiceDeafen: () => set((state) => {
            const nextDeafened = !state.voiceConnection.deafened;
            const nextMuted = nextDeafened ? true : state.voiceConnection.muted;
            VoiceService_1.voiceService.toggleDeafen(nextDeafened).catch(console.error);
            TabSyncService_1.tabSyncService.broadcastDeafen(nextDeafened);
            if (nextDeafened) {
                AudioService_1.audioService.playDeafen();
            }
            else {
                AudioService_1.audioService.playUndeafen();
            }
            return {
                voiceConnection: { ...state.voiceConnection, deafened: nextDeafened, muted: nextMuted },
                voiceParticipants: state.voiceParticipants.map((p) => p.isLocal ? { ...p, muted: nextMuted, deafened: nextDeafened } : p),
            };
        }),
        toggleVoiceCamera: () => set((state) => {
            const nextCamera = !state.voiceConnection.cameraEnabled;
            VoiceService_1.voiceService.toggleVideo(nextCamera).catch(console.error);
            AudioService_1.audioService.playToggleClick();
            return {
                voiceConnection: { ...state.voiceConnection, cameraEnabled: nextCamera },
                voiceParticipants: state.voiceParticipants.map((p) => p.isLocal ? { ...p, cameraEnabled: nextCamera } : p),
            };
        }),
        toggleVoiceScreenShare: async (sourceId, withAudio = true) => {
            const { voiceConnection } = get();
            // voiceService is imported directly
            const nextSharing = !voiceConnection.screenSharing;
            VoiceService_1.voiceService
                .toggleScreenShare(nextSharing, sourceId, withAudio)
                .then(() => {
                set((current) => ({
                    voiceConnection: {
                        ...current.voiceConnection,
                        screenSharing: nextSharing,
                    },
                    voiceParticipants: current.voiceParticipants.map((p) => p.isLocal ? { ...p, screenSharing: nextSharing } : p),
                }));
            })
                .catch((err) => {
                console.error('[Store] Screen share toggle failed:', err);
            });
        },
        toggleNoiseSuppression: () => {
            const current = get().voiceConnection.noiseSuppression ?? (0, realtimeConfig_1.getNoiseSuppressionEnabled)();
            const next = !current;
            (0, realtimeConfig_1.setNoiseSuppressionEnabled)(next);
            VoiceService_1.voiceService.setNoiseSuppression(next).catch(console.error);
            AudioService_1.audioService.playToggleClick();
            set((state) => ({
                voiceConnection: { ...state.voiceConnection, noiseSuppression: next },
            }));
        },
        setNoiseEngine: (engine) => {
            (0, realtimeConfig_1.setNoiseEngine)(engine);
            const enabled = get().voiceConnection.noiseSuppression ?? (0, realtimeConfig_1.getNoiseSuppressionEnabled)();
            VoiceService_1.voiceService.setNoiseSuppression(enabled, engine).catch(console.error);
            set((state) => ({
                voiceConnection: { ...state.voiceConnection, noiseEngine: engine },
            }));
        },
        setVoiceViewVisible: (visible) => set((state) => {
            const channelId = state.voiceConnection.channelId ?? state.voiceViewChannelId;
            return {
                voiceViewVisible: visible,
                voiceViewChannelId: channelId,
            };
        }),
        fetchMessages: async (channelId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/messages?channelId=${channelId}`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    const formattedMessages = data.map((m) => ({
                        id: String(m.id),
                        content: m.content,
                        authorId: String(m.authorId),
                        authorName: m.authorName,
                        authorAvatar: m.authorAvatar,
                        channelId: String(m.channel_id),
                        timestamp: new Date(m.timestamp.replace(/-/g, '/')),
                        parentId: m.parent_id ? String(m.parent_id) : undefined,
                        attachments: m.attachments || []
                    }));
                    set({ messages: formattedMessages });
                }
                else if (response.status === 403) {
                    const data = await response.json().catch(() => ({}));
                    if (data.is_banned || data.is_system_banned) {
                        alert('Hesabınız sistem yöneticisi tarafından yasaklanmıştır.');
                        set({ currentUser: null, selectedServerId: null, selectedChannelId: null });
                    }
                    else if (data.error && data.error.includes('yasaklandınız')) {
                        set((state) => ({
                            servers: state.servers.filter(s => s.id !== state.selectedServerId),
                            selectedServerId: null,
                            selectedChannelId: null,
                            messages: []
                        }));
                    }
                }
            }
            catch (error) {
                console.error('Failed to fetch messages:', error);
            }
        },
        sendMessage: async (content, channelId, attachments) => {
            const state = get();
            const userId = state.currentUser?.id || '1';
            const activeReply = state.activeReply;
            // Check delivery mode: if cPanel legacy mode is selected
            if ((0, realtimeConfig_1.getMessageDeliveryMode)() === 'cpanel') {
                try {
                    const response = await fetch(`${exports.API_BASE_URL}/messages`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            content,
                            channelId,
                            userId,
                            parentId: activeReply,
                            attachments: attachments
                        }),
                        credentials: 'include',
                    });
                    if (response.ok) {
                        set({ activeReply: null });
                        AudioService_1.audioService.playMessageSent();
                        await get().fetchMessages(channelId);
                    }
                }
                catch (err) {
                    console.error('cPanel direct message send failed:', err);
                }
                return;
            }
            // Default WebSocket Mode: Optimistic Message with stable clientKey (0ms delay!)
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
            const optimisticMsg = {
                id: tempId,
                clientKey: tempId,
                content,
                authorId: String(userId),
                authorName: state.currentUser?.displayName || state.currentUser?.username || 'Kullanıcı',
                authorAvatar: state.currentUser?.avatar,
                channelId: String(channelId),
                timestamp: new Date(),
                parentId: activeReply ? String(activeReply) : undefined,
                attachments: attachments ? attachments.map((a, i) => ({
                    id: `att_${Date.now()}_${i}`,
                    url: a.url,
                    filename: a.filename,
                    type: a.type
                })) : [],
            };
            set((s) => ({
                messages: [...s.messages, optimisticMsg],
                activeReply: null,
            }));
            AudioService_1.audioService.playMessageSent();
            // 2. Realtime Broadcast over Socket.io to VDS immediately (<10ms)
            RealtimeService_1.realtimeService.broadcastMessage({
                channelId,
                message: optimisticMsg,
            });
            // 3. Background Database Save (Non-blocking: user sees their message instantly)
            try {
                const response = await fetch(`${exports.API_BASE_URL}/messages`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        content,
                        channelId,
                        userId,
                        parentId: activeReply,
                        attachments: attachments
                    }),
                    credentials: 'include',
                });
                const data = await response.json().catch(() => ({}));
                if (response.status === 403) {
                    // Rollback optimistic message on error/ban
                    set((s) => ({
                        messages: s.messages.filter((m) => m.id !== tempId),
                    }));
                    if (data.is_banned || data.is_system_banned) {
                        set({ currentUser: null, selectedServerId: null, selectedChannelId: null, _banMessage: 'Hesabınız sistem yöneticisi tarafından yasaklanmıştır.' });
                        return;
                    }
                    if (data.error && data.error.includes('yasaklandınız')) {
                        set((s) => ({
                            servers: s.servers.filter(srv => srv.id !== s.selectedServerId),
                            selectedServerId: null,
                            selectedChannelId: null,
                            messages: []
                        }));
                        alert(data.error);
                        return;
                    }
                    alert(data.message || data.error || 'Mesaj gönderilemedi.');
                    return;
                }
                if (response.ok && data.id) {
                    const realId = String(data.id);
                    // Reconcile temporary ID with permanent database Snowflake ID while keeping clientKey stable
                    set((s) => ({
                        messages: s.messages.map((m) => (m.id === tempId ? { ...m, id: realId, clientKey: m.clientKey || tempId } : m)),
                    }));
                }
            }
            catch (error) {
                console.error('Background DB save failed for message:', error);
            }
        },
        togglePinMessage: async (messageId, isDM, pinState) => {
            try {
                // Optimistic UI Update
                if (isDM) {
                    set(state => ({
                        directMessages: state.directMessages.map(msg => msg.id === messageId ? { ...msg, isPinned: pinState } : msg)
                    }));
                }
                else {
                    set(state => ({
                        messages: state.messages.map(msg => msg.id === messageId ? { ...msg, isPinned: pinState } : msg)
                    }));
                }
                const response = await fetch(`${exports.API_BASE_URL}/messages/pin`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messageId, isDM, pinState: pinState ? 1 : 0 }),
                    credentials: 'include',
                });
                if (!response.ok) {
                    // Rollback on error
                    if (isDM) {
                        await get().fetchDMs(get().selectedDMUserId);
                    }
                    else {
                        await get().fetchMessages(get().selectedChannelId);
                    }
                }
            }
            catch (error) {
                console.error('Failed to toggle pin:', error);
                // Rollback on error
                if (isDM) {
                    await get().fetchDMs(get().selectedDMUserId);
                }
                else {
                    await get().fetchMessages(get().selectedChannelId);
                }
            }
        },
        reportMessage: async (messageId, reason) => {
            const state = get();
            const userId = state.currentUser?.id || '1';
            try {
                const response = await fetch(`${exports.API_BASE_URL}/messages/report`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messageId, userId, reason }),
                    credentials: 'include',
                });
                if (response.ok) {
                    alert("Mesaj başarıyla şikayet edildi. Admin inceleyecek.");
                }
            }
            catch (error) {
                console.error('Failed to report message:', error);
            }
        },
        fetchServers: async () => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    const formattedServers = data.map((s) => ({
                        id: String(s.id),
                        name: s.name,
                        ownerId: String(s.owner_id),
                        icon: s.icon || s.name.charAt(0).toUpperCase(),
                        createdAt: new Date(s.created_at),
                        members: s.members ? s.members.map(String) : [],
                        inviteCode: s.invite_code ? String(s.invite_code) : undefined,
                        banner: s.banner ? String(s.banner) : undefined,
                        categories: s.categories ? s.categories.map((cat) => ({
                            id: String(cat.id),
                            name: cat.name,
                            serverId: String(cat.server_id),
                            collapsed: false
                        })) : [],
                        channels: s.channels.map((c) => ({
                            id: String(c.id),
                            name: c.name,
                            type: c.type,
                            serverId: String(c.server_id),
                            categoryId: c.category_id ? String(c.category_id) : undefined
                        }))
                    }));
                    // Sunucu listesini güncelle
                    const channels = formattedServers.flatMap(s => s.channels);
                    set({
                        servers: formattedServers,
                        channels: channels
                    });
                    // Eğer persist edilen kanal/sunucu yoksa veya artık mevcut değilse ilk kanalı seç
                    const lastChannelId = get().selectedChannelId;
                    const channelExists = channels.some(c => c.id === lastChannelId);
                    if (formattedServers.length === 0) {
                        set({
                            selectedServerId: null,
                            selectedChannelId: null
                        });
                        localStorage.removeItem('meci_last_server');
                        localStorage.removeItem('meci_last_channel');
                    }
                    else if (!channelExists && formattedServers[0].channels.length > 0) {
                        const firstServer = formattedServers[0];
                        const firstChannel = firstServer.channels[0];
                        set({
                            selectedServerId: firstServer.id,
                            selectedChannelId: firstChannel.id
                        });
                        localStorage.setItem('meci_last_server', firstServer.id);
                        localStorage.setItem('meci_last_channel', firstChannel.id);
                    }
                    else if (channelExists) {
                        // Kanal varsa sunucuyu da güncelle (tutarlı olsun)
                        const channel = channels.find(c => c.id === lastChannelId);
                        if (channel) {
                            set({ selectedServerId: channel.serverId });
                            localStorage.setItem('meci_last_server', channel.serverId);
                        }
                    }
                }
            }
            catch (error) {
                console.error('Failed to fetch servers:', error);
            }
        },
        createServer: async (name) => {
            const state = get();
            const owner_id = state.currentUser?.id || '1';
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name, ownerId: owner_id }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await get().fetchServers();
                }
            }
            catch (error) {
                console.error('Failed to create server:', error);
            }
        },
        createChannel: async (serverId, name, type, categoryId) => {
            const { currentUser, servers } = get();
            if (!currentUser)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/channels`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, name, type, categoryId, userId: currentUser.id })
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok)
                    throw new Error(data.error || 'Kanal oluşturulamadı');
                await get().fetchServers();
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        updateChannel: async (serverId, channelId, name) => {
            const { currentUser } = get();
            if (!currentUser)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/channels/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, channelId, name, userId: currentUser.id })
                });
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Kanal güncellenemedi');
                await get().fetchServers();
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        deleteChannel: async (serverId, channelId) => {
            const { currentUser } = get();
            if (!currentUser)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/channels/delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, channelId, userId: currentUser.id })
                });
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Kanal silinemedi');
                const { selectedChannelId } = get();
                if (selectedChannelId === channelId) {
                    set({ selectedChannelId: null });
                }
                await get().fetchServers();
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        createCategory: async (serverId, name) => {
            const { currentUser } = get();
            if (!currentUser)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/categories/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, name, userId: currentUser.id })
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok)
                    throw new Error(data.error || 'Kategori oluşturulamadı');
                await get().fetchServers();
            }
            catch (error) {
                console.error('Failed to create category:', error);
                throw error;
            }
        },
        updateCategory: async (serverId, categoryId, name) => {
            const { currentUser } = get();
            if (!currentUser)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/categories/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, categoryId, name, userId: currentUser.id })
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok)
                    throw new Error(data.error || 'Kategori güncellenemedi');
                await get().fetchServers();
            }
            catch (error) {
                console.error('Failed to update category:', error);
                throw error;
            }
        },
        deleteCategory: async (serverId, categoryId) => {
            const { currentUser } = get();
            if (!currentUser)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/categories/delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, categoryId, userId: currentUser.id })
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok)
                    throw new Error(data.error || 'Kategori silinemedi');
                await get().fetchServers();
            }
            catch (error) {
                console.error('Failed to delete category:', error);
                throw error;
            }
        },
        reorderCategories: async (serverId, orderedCategories) => {
            const { servers } = get();
            // Optimistic local update
            const updatedServers = servers.map(s => {
                if (s.id === serverId) {
                    return { ...s, categories: orderedCategories };
                }
                return s;
            });
            set({ servers: updatedServers });
            try {
                const positions = orderedCategories.map((c, index) => ({
                    id: c.id,
                    position: index
                }));
                await fetch(`${exports.API_BASE_URL}/servers/categories/reorder`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, positions }),
                    credentials: 'include'
                });
            }
            catch (error) {
                console.error('Failed to reorder categories:', error);
                await get().fetchServers();
            }
        },
        updateServer: async (serverId, data) => {
            const state = get();
            const userId = state.currentUser?.id;
            if (!userId)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, ...data, userId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await get().fetchServers();
                }
            }
            catch (error) {
                console.error('Failed to update server:', error);
            }
        },
        deleteServer: async (serverId) => {
            const state = get();
            const userId = state.currentUser?.id;
            if (!userId)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, userId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    set({ selectedServerId: null });
                    await get().fetchServers();
                }
            }
            catch (error) {
                console.error('Failed to delete server:', error);
            }
        },
        // --- Member Management ---
        currentServerRoles: [],
        currentServerInvites: [],
        currentServerVanityUrl: null,
        currentServerInvitesPausedUntil: null,
        currentServerInvitesPaused: false,
        fetchServerInvites: async (serverId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/invites?serverId=${serverId}`, {
                    credentials: 'include'
                });
                const data = await response.json();
                if (response.ok) {
                    set({
                        currentServerInvites: data.invites || [],
                        currentServerVanityUrl: data.vanity_url || null,
                        currentServerInvitesPausedUntil: data.invites_paused_until || null,
                        currentServerInvitesPaused: data.is_invites_paused || false
                    });
                }
            }
            catch (e) {
                console.error('fetchServerInvites error', e);
            }
        },
        createServerInvite: async (serverId, maxUses = 0, expiresIn = undefined) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/invites/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, maxUses, expiresIn }),
                    credentials: 'include'
                });
                if (response.ok) {
                    get().fetchServerInvites(serverId);
                }
            }
            catch (e) {
                console.error('createServerInvite error', e);
            }
        },
        deleteServerInvite: async (inviteId, serverId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/invites/delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ inviteId, serverId }),
                    credentials: 'include'
                });
                if (response.ok) {
                    const { selectedServerId } = get();
                    if (selectedServerId) {
                        get().fetchServerInvites(selectedServerId);
                    }
                }
            }
            catch (e) {
                console.error('deleteServerInvite error', e);
            }
        },
        updateServerVanityUrl: async (serverId, vanityUrl) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/vanity/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, vanityUrl }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (response.ok) {
                    set({ currentServerVanityUrl: data.vanity_url });
                    get().fetchServerInvites(serverId);
                    return data;
                }
                else {
                    throw new Error(data.error || 'Bilinmeyen hata');
                }
            }
            catch (e) {
                console.error('updateServerVanityUrl error', e);
                throw e;
            }
        },
        pauseServerInvites: async (serverId, duration) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/invites/pause`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, duration }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (response.ok) {
                    set({
                        currentServerInvitesPausedUntil: data.invites_paused_until,
                        currentServerInvitesPaused: data.is_invites_paused || false
                    });
                    get().fetchServerInvites(serverId);
                }
                else {
                    throw new Error(data.error || 'Bilinmeyen hata');
                }
            }
            catch (e) {
                console.error('pauseServerInvites error', e);
                throw e;
            }
        },
        currentServerMembers: [],
        fetchServerMembers: async (serverId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/members?serverId=${serverId}`);
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Üyeler alınamadı');
                const mapped = data.map((m) => ({
                    ...m,
                    avatar: getImageUrl(m.avatar, 'avatar')
                }));
                set({ currentServerMembers: mapped });
                return mapped;
            }
            catch (error) {
                console.error(error);
                return [];
            }
        },
        serverVoiceStates: {},
        fetchVoiceStates: async (serverId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/voice/state?serverId=${serverId}`);
                if (!response.ok)
                    return;
                const data = await response.json();
                if (data && typeof data === 'object') {
                    set((prev) => {
                        // Ensure local user is always in voiceParticipants if connected
                        const conn = prev.voiceConnection;
                        const currentChannelId = conn.channelId;
                        let newParticipants = [...prev.voiceParticipants];
                        if (currentChannelId && conn.status !== 'disconnected' && prev.currentUser) {
                            const localExists = newParticipants.some((p) => p.isLocal);
                            if (!localExists) {
                                const rawUid = Number(prev.currentUser.id);
                                const uid = Number.isInteger(rawUid) && rawUid > 0 ? rawUid : 0;
                                newParticipants = [
                                    buildParticipant(prev, uid, { muted: conn.muted, isLocal: true }),
                                    ...newParticipants,
                                ];
                            }
                        }
                        // Merge serverVoiceStates data into voiceParticipants to resolve names
                        // serverVoiceStates contains real userId -> displayName mapping from the DB
                        if (currentChannelId && data[currentChannelId]) {
                            const serverUsers = data[currentChannelId];
                            serverUsers.forEach((su) => {
                                const suId = String(su.userId || su.id);
                                const isLocalUser = String(prev.currentUser?.id) === suId;
                                const existing = newParticipants.find((p) => String(p.userId) === suId || (isLocalUser && p.isLocal));
                                if (!existing && !isLocalUser) {
                                    // If we are currently connected in this voice channel, play join chime
                                    if (conn.status === 'connected' && conn.channelId === currentChannelId) {
                                        AudioService_1.audioService.playJoinVoice();
                                    }
                                    // Add remote user from server that Agora hasn't reported yet
                                    newParticipants.push({
                                        id: `voice-db-${suId}`,
                                        userId: suId,
                                        displayName: su.displayName || su.username || `Kullanıcı ${suId}`,
                                        avatar: su.avatar,
                                        accentColor: su.accentColor || '#b48c5c',
                                        muted: !!su.muted,
                                        deafened: !!su.deafened,
                                        cameraEnabled: !!su.cameraEnabled,
                                        screenSharing: !!su.screenSharing,
                                        speaking: false,
                                        isLocal: false,
                                    });
                                }
                                else if (existing && !existing.isLocal) {
                                    // Update name/avatar for existing remote participant (fix UID-only display)
                                    const idx = newParticipants.indexOf(existing);
                                    newParticipants[idx] = {
                                        ...existing,
                                        displayName: su.displayName || su.username || existing.displayName,
                                        avatar: su.avatar || existing.avatar,
                                        muted: !!su.muted,
                                        deafened: !!su.deafened,
                                    };
                                }
                            });
                            // Synchronize with live WebRTC snapshot to ALWAYS preserve and update real connected peers!
                            let livePeerIds = [];
                            try {
                                const liveSnapshot = VoiceService_1.voiceService.getLiveSnapshot();
                                if (liveSnapshot?.connected && liveSnapshot.peers) {
                                    livePeerIds = liveSnapshot.peers.map((p) => String(p.userId));
                                    liveSnapshot.peers.forEach((peer) => {
                                        const pId = String(peer.userId);
                                        if (pId === String(prev.currentUser?.id))
                                            return;
                                        const idx = newParticipants.findIndex((p) => String(p.userId) === pId);
                                        if (idx === -1) {
                                            newParticipants.push(buildParticipant(prev, peer.userId, {
                                                displayName: peer.displayName,
                                                muted: !!peer.muted,
                                                deafened: !!peer.deafened,
                                                cameraEnabled: !!peer.camera,
                                                screenSharing: !!peer.screen,
                                                isLocal: false,
                                            }));
                                        }
                                        else {
                                            newParticipants[idx] = {
                                                ...newParticipants[idx],
                                                displayName: peer.displayName || newParticipants[idx].displayName,
                                                muted: !!peer.muted,
                                                deafened: !!peer.deafened,
                                                cameraEnabled: !!peer.camera,
                                                screenSharing: !!peer.screen,
                                            };
                                        }
                                    });
                                }
                            }
                            catch (e) { }
                            // Remove participants ONLY if they are not local, not bot, not in WebRTC, and not in server state
                            const serverIds = serverUsers.map((su) => String(su.userId || su.id));
                            newParticipants = newParticipants.filter((p) => p.isLocal || p.isBot || String(p.userId) === '370846327506079745' || (p.displayName && p.displayName.toLowerCase().includes('bot')) || livePeerIds.includes(String(p.userId)) || serverIds.includes(String(p.userId)));
                        }
                        return {
                            serverVoiceStates: data,
                            voiceParticipants: newParticipants,
                        };
                    });
                }
            }
            catch (e) {
                console.warn('[VoiceState] Failed to fetch voice states:', e);
            }
        },
        kickServerMember: async (serverId, userId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/members/kick`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, userId }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Üye atılamadı');
                await get().fetchServerMembers(serverId);
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        banServerMember: async (serverId, userId, reason, deleteMessageHistory) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/members/ban`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, userId, reason, deleteMessageHistory }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Üye banlanamadı');
                // 1. Immediately purge user from currentServerMembers and users in state
                set((state) => ({
                    currentServerMembers: state.currentServerMembers.filter(m => String(m.id) !== String(userId)),
                    // 2. If message deletion requested, purge user messages from active view immediately
                    messages: (deleteMessageHistory && deleteMessageHistory !== 'none')
                        ? state.messages.filter(msg => String(msg.authorId) !== String(userId))
                        : state.messages
                }));
                // 3. Re-fetch current server members and messages
                await get().fetchServerMembers(serverId);
                const { selectedChannelId } = get();
                if (selectedChannelId) {
                    await get().fetchMessages(selectedChannelId);
                }
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        unbanServerMember: async (serverId, userId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/members/unban`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, userId }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Yasak kaldırılamadı');
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        fetchServerBans: async (serverId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/bans?serverId=${serverId}`, {
                    credentials: 'include'
                });
                if (!response.ok)
                    return [];
                return await response.json();
            }
            catch (error) {
                console.error(error);
                return [];
            }
        },
        transferOwnership: async (serverId, newOwnerId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/transfer-ownership`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, newOwnerId }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (!response.ok)
                    throw new Error(data.error || 'Sahiplik devredilemedi');
                await get().fetchServers();
                await get().fetchServerMembers(serverId);
            }
            catch (error) {
                console.error(error);
                throw error;
            }
        },
        assignRoleToMember: async (serverId, userId, roleIds) => {
            const { currentUser } = get();
            try {
                const res = await fetch(`${exports.API_BASE_URL}/servers/roles/assign`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, userId, roleIds, requesterId: currentUser?.id })
                });
                const data = await res.json();
                if (data.status === 'success') {
                    await get().fetchServerMembers(serverId);
                }
                else {
                    throw new Error(data.error || 'Roller güncellenemedi');
                }
            }
            catch (error) {
                console.error("assignRoleToMember error:", error);
                throw error;
            }
        },
        updateMemberNickname: async (serverId, userId, nickname) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/members/nickname`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ serverId, userId, nickname })
                });
                const data = await response.json();
                if (response.ok) {
                    await get().fetchServerMembers(serverId);
                }
                else {
                    throw new Error(data.error || 'İsim güncellenemedi');
                }
            }
            catch (error) {
                console.error("updateMemberNickname error:", error);
                throw error;
            }
        },
        fetchServerRoles: async (serverId) => {
            try {
                const res = await fetch(`${exports.API_BASE_URL}/servers/roles?serverId=${serverId}`);
                const roles = await res.json();
                if (Array.isArray(roles)) {
                    set({ currentServerRoles: roles });
                    return roles;
                }
                else {
                    set({ currentServerRoles: [] });
                    return [];
                }
            }
            catch (error) {
                console.error('Roller çekilemedi:', error);
                set({ currentServerRoles: [] });
                return [];
            }
        },
        createServerRole: async (serverId, name, color) => {
            const { currentUser } = get();
            try {
                const res = await fetch(`${exports.API_BASE_URL}/servers/roles`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, name, color, userId: currentUser?.id })
                });
                const data = await res.json();
                if (data.status === 'success') {
                    await get().fetchServerRoles(serverId);
                }
                else {
                    throw new Error(data.error);
                }
            }
            catch (error) {
                console.error('Rol oluşturulamadı:', error);
                throw error;
            }
        },
        updateServerRole: async (roleId, name, color, permissions) => {
            const { currentUser, selectedServerId } = get();
            try {
                const res = await fetch(`${exports.API_BASE_URL}/servers/roles`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ roleId, name, color, permissions, userId: currentUser?.id })
                });
                const data = await res.json();
                if (data.status === 'success' && selectedServerId) {
                    await get().fetchServerRoles(selectedServerId);
                }
                else if (data.error) {
                    throw new Error(data.error);
                }
            }
            catch (error) {
                console.error('Rol güncellenemedi:', error);
                throw error;
            }
        },
        deleteServerRole: async (roleId) => {
            const { currentUser, selectedServerId } = get();
            try {
                const res = await fetch(`${exports.API_BASE_URL}/servers/roles`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ roleId, userId: currentUser?.id })
                });
                const data = await res.json();
                if (data.status === 'success' && selectedServerId) {
                    await get().fetchServerRoles(selectedServerId);
                }
                else if (data.error) {
                    throw new Error(data.error);
                }
            }
            catch (error) {
                console.error('Rol silinemedi:', error);
                throw error;
            }
        },
        leaveServer: () => {
            set({ selectedServerId: null, selectedChannelId: null });
        },
        fetchUsers: async () => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/users`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    const formattedUsers = data.map((u) => ({
                        ...u,
                        id: String(u.id),
                        username: u.username,
                        displayName: u.display_name || u.username,
                        customStatus: u.custom_status,
                        createdAt: u.created_at ? new Date(u.created_at) : undefined,
                        roles: u.id === '1' ? ['Admin'] : [],
                        status: u.status || 'offline'
                    }));
                    // Eğer kullanıcı listede yoksa ekle (kendini her zaman görmeli)
                    const currentUser = get().currentUser;
                    if (currentUser && !formattedUsers.some(u => u.id === currentUser.id)) {
                        formattedUsers.push(currentUser);
                    }
                    set({ users: formattedUsers });
                }
                else {
                    console.error('Fetch users failed:', response.status);
                }
            }
            catch (error) {
                console.error('Failed to fetch users:', error);
            }
        },
        claimOwnership: async (serverId) => {
            const state = get();
            const userId = state.currentUser?.id;
            if (!userId)
                return;
            try {
                const response = await fetch(`${exports.API_BASE_URL}/servers/claim-ownership`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ serverId, userId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await get().fetchServers();
                }
            }
            catch (error) {
                console.error('Failed to claim ownership:', error);
            }
        },
        login: async (email, password) => {
            try {
                const deviceInfo = await (0, exports.getDeviceInfo)('Standard Login');
                const response = await fetch(`${exports.API_BASE_URL}/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password, ...deviceInfo }),
                    credentials: 'include',
                });
                if (response.status === 426) {
                    set({ isVersionBlocked: true });
                    throw new Error('Sürümünüz artık desteklenmemektedir.');
                }
                const data = await response.json();
                if (response.status === 403 && (data.is_banned || data.is_system_banned)) {
                    const banMsg = data.error || '🚫 Hesabınız sistem yöneticisi tarafından yasaklanmıştır.';
                    set({ _banMessage: banMsg, currentUser: null });
                    throw new Error(banMsg);
                }
                if (response.ok) {
                    set({ currentUser: mapUser(data.user) });
                }
                else {
                    throw new Error(data.error || 'Giriş başarısız');
                }
            }
            catch (error) {
                throw error;
            }
        },
        loginWithGoogle: async (token) => {
            try {
                const deviceInfo = await (0, exports.getDeviceInfo)('Google OAuth');
                const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
                const path = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
                const redirectUri = origin;
                const response = await fetch(`${exports.API_BASE_URL}/auth/google`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, redirect_uri: redirectUri, ...deviceInfo }),
                    credentials: 'include',
                });
                if (response.status === 426) {
                    set({ isVersionBlocked: true });
                    throw new Error('Sürümünüz artık desteklenmemektedir.');
                }
                const data = await response.json();
                if (response.status === 403 && (data.is_banned || data.is_system_banned)) {
                    const banMsg = data.error || '🚫 Hesabınız sistem yöneticisi tarafından yasaklanmıştır.';
                    set({ _banMessage: banMsg, currentUser: null });
                    throw new Error(banMsg);
                }
                if (response.ok) {
                    set({ currentUser: mapUser(data.user) });
                }
                else {
                    throw new Error(data.error || 'Google girişi başarısız');
                }
            }
            catch (error) {
                throw error;
            }
        },
        loginWithFlexable: async (flexableData) => {
            try {
                const deviceInfo = await (0, exports.getDeviceInfo)('Flexable OAuth');
                const payload = { ...deviceInfo };
                if (typeof flexableData === 'string') {
                    payload.token = flexableData;
                }
                else if (flexableData) {
                    payload.token = flexableData.accessToken || flexableData.token;
                    payload.code = flexableData.code;
                    payload.email = flexableData.email;
                    payload.name = flexableData.displayName || flexableData.name;
                    payload.sub = flexableData.sub;
                    payload.picture = flexableData.picture;
                    payload.plan = flexableData.plan;
                }
                const response = await fetch(`${exports.API_BASE_URL}/auth/flexable`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    credentials: 'include',
                });
                if (response.status === 426) {
                    set({ isVersionBlocked: true });
                    throw new Error('Sürümünüz artık desteklenmemektedir.');
                }
                const data = await response.json();
                if (response.status === 403 && (data.is_banned || data.is_system_banned)) {
                    const banMsg = data.error || '🚫 Hesabınız sistem yöneticisi tarafından yasaklanmıştır.';
                    set({ _banMessage: banMsg, currentUser: null });
                    throw new Error(banMsg);
                }
                if (response.ok && data.user) {
                    set({ currentUser: mapUser(data.user) });
                    const dName = (flexableData && (flexableData.displayName || flexableData.name)) || data.user.display_name;
                    if (dName) {
                        localStorage.setItem('meci_conn_flexable', dName);
                        if (flexableData?.email || data.user.email)
                            localStorage.setItem('meci_conn_flexable_email', flexableData?.email || data.user.email);
                        if (flexableData?.sub)
                            localStorage.setItem('meci_conn_flexable_sub', flexableData.sub);
                        if (flexableData?.picture || data.user.avatar)
                            localStorage.setItem('meci_conn_flexable_avatar', flexableData?.picture || data.user.avatar);
                        if (flexableData?.plan)
                            localStorage.setItem('meci_conn_flexable_plan', flexableData.plan);
                        if (flexableData?.accessToken)
                            localStorage.setItem('meci_conn_flexable_token', flexableData.accessToken);
                        localStorage.setItem('meci_conn_flexable_show', 'true');
                    }
                }
                else {
                    throw new Error(data.error || 'Flexable girişi başarısız');
                }
            }
            catch (error) {
                throw error;
            }
        },
        register: async (username, email, password) => {
            try {
                const deviceInfo = await (0, exports.getDeviceInfo)('Standard Register');
                const response = await fetch(`${exports.API_BASE_URL}/auth/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, email, password, ...deviceInfo }),
                    credentials: 'include',
                });
                if (response.status === 426) {
                    set({ isVersionBlocked: true });
                    throw new Error('Sürümünüz artık desteklenmemektedir.');
                }
                const data = await response.json();
                if (response.ok) {
                    set({ currentUser: mapUser(data.user) });
                }
                else {
                    throw new Error(data.error || 'Kayıt başarısız');
                }
            }
            catch (error) {
                throw error;
            }
        },
        logout: async () => {
            try {
                await fetch(`${exports.API_BASE_URL}/auth/logout`, {
                    credentials: 'include'
                });
                set({ currentUser: null });
            }
            catch (error) {
                console.error('Logout failed:', error);
                set({ currentUser: null });
            }
        },
        checkAuth: async () => {
            try {
                const deviceInfo = await (0, exports.getDeviceInfo)('Auto Heartbeat');
                const stringifiedInfo = {};
                Object.entries(deviceInfo).forEach(([key, val]) => {
                    stringifiedInfo[key] = String(val);
                });
                const queryParams = new URLSearchParams(stringifiedInfo).toString();
                const response = await fetch(`${exports.API_BASE_URL}/auth/me?${queryParams}`, {
                    credentials: 'include'
                });
                if (response.status === 426) {
                    set({ isVersionBlocked: true, currentUser: null });
                    return;
                }
                if (response.status === 403) {
                    const data = await response.json().catch(() => ({}));
                    if (data.is_banned || data.is_system_banned) {
                        set({ currentUser: null, _banMessage: 'Hesabınız sistem yöneticisi tarafından yasaklanmıştır.' });
                    }
                    else {
                        set({ currentUser: null });
                    }
                    return;
                }
                if (response.ok) {
                    const user = await response.json();
                    const mapped = mapUser(user);
                    set({ currentUser: mapped });
                    // Verileri çek
                    await get().fetchUsers();
                    await get().fetchServers();
                }
                else {
                    set({ currentUser: null });
                }
            }
            catch (error) {
                set({ currentUser: null });
            }
        },
        updateProfile: async (data) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/users/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data),
                    credentials: 'include',
                });
                const result = await response.json();
                if (response.ok) {
                    set({ currentUser: mapUser(result.user) });
                }
                else {
                    throw new Error(result.error || 'Profil güncelleme başarısız');
                }
            }
            catch (error) {
                throw error;
            }
        },
        fetchFriends: async () => {
            try {
                get().fetchBlockedUsers().catch(() => { });
                const response = await fetch(`${exports.API_BASE_URL}/friends`, { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    set({
                        friends: data.map((f) => ({
                            ...mapUser(f),
                            friendshipStatus: f.friendship_status || 'accepted',
                            requesterId: f.requester_id ? String(f.requester_id) : undefined,
                            addresseeId: f.addressee_id ? String(f.addressee_id) : undefined,
                        }))
                    });
                }
            }
            catch (error) {
                console.error('Failed to fetch friends:', error);
            }
        },
        addFriend: async (target) => {
            try {
                const payload = typeof target === 'string'
                    ? (target.startsWith('@') || isNaN(Number(target)) ? { username: target } : { friendId: target })
                    : target;
                const response = await fetch(`${exports.API_BASE_URL}/friends`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    credentials: 'include',
                });
                const data = await response.json();
                if (response.ok) {
                    await get().fetchFriends();
                    try {
                        const targetUserId = data.friend_id || payload.friendId;
                        RealtimeService_1.realtimeService.getSocket().emit('friend:sync', { targetUserId, type: 'request' });
                        RealtimeService_1.realtimeService.getSocket().emit('friend:request', { targetUserId, requesterId: get().currentUser?.id });
                    }
                    catch (_) { }
                    return { success: true, message: data.message || 'Arkadaşlık isteği gönderildi.' };
                }
                else {
                    return { success: false, error: data.error || 'İstek gönderilemedi.' };
                }
            }
            catch (error) {
                console.error('Failed to add friend:', error);
                return { success: false, error: error.message || 'Bağlantı hatası' };
            }
        },
        acceptFriendRequest: async (friendId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/friends/accept`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ friendId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await get().fetchFriends();
                    try {
                        RealtimeService_1.realtimeService.getSocket().emit('friend:sync', { targetUserId: friendId, type: 'accepted' });
                        RealtimeService_1.realtimeService.getSocket().emit('friend:accepted', { targetUserId: friendId, userId: get().currentUser?.id });
                    }
                    catch (_) { }
                }
            }
            catch (error) {
                console.error('Failed to accept friend request:', error);
            }
        },
        removeFriend: async (friendId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/friends/remove`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ friendId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await get().fetchFriends();
                    try {
                        RealtimeService_1.realtimeService.getSocket().emit('friend:sync', { targetUserId: friendId, type: 'removed' });
                    }
                    catch (_) { }
                }
            }
            catch (error) {
                console.error('Failed to remove friend or request:', error);
            }
        },
        fetchDMs: async (otherId) => {
            try {
                RealtimeService_1.realtimeService.joinChatDM(get().currentUser?.id || '', otherId);
                // Background ping and mark delivered
                get().pingUser().catch(() => { });
                get().markDMsAsDelivered().catch(() => { });
                const response = await fetch(`${exports.API_BASE_URL}/dms?otherId=${otherId}`, { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    set({ directMessages: data });
                }
            }
            catch (error) {
                console.error('Failed to fetch DMs:', error);
            }
        },
        sendDM: async (otherId, content, parentId) => {
            const state = get();
            const resolvedParentId = parentId ?? state.activeReply ?? null;
            const currentUserId = String(state.currentUser?.id || '1');
            // 1. Optimistic UI update: message appears on screen with 0ms delay!
            const tempId = `temp_dm_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
            const optimisticDM = {
                id: tempId,
                clientKey: tempId,
                content,
                sender_id: currentUserId,
                sender_name: state.currentUser?.displayName || state.currentUser?.username,
                sender_avatar: state.currentUser?.avatar,
                receiver_id: String(otherId),
                created_at: new Date().toISOString(),
                parentId: resolvedParentId,
            };
            set((s) => ({
                directMessages: [...s.directMessages, optimisticDM],
                activeReply: null,
            }));
            AudioService_1.audioService.playMessageSent();
            // 2. Realtime Broadcast over Socket.io immediately to VDS
            RealtimeService_1.realtimeService.broadcastMessage({
                dmUserId: otherId,
                currentUserId: currentUserId,
                message: optimisticDM,
            });
            // 3. Background Database Save (Non-blocking)
            try {
                const response = await fetch(`${exports.API_BASE_URL}/dms`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ receiverId: otherId, content, parentId: resolvedParentId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json().catch(() => ({}));
                    if (data.id) {
                        const realId = String(data.id);
                        set((s) => ({
                            directMessages: s.directMessages.map((m) => (m.id === tempId ? { ...m, id: realId, clientKey: m.clientKey || tempId } : m)),
                        }));
                    }
                }
                else {
                    // Rollback optimistic DM on error
                    set((s) => ({
                        directMessages: s.directMessages.filter((m) => m.id !== tempId),
                    }));
                    const err = await response.json().catch(() => ({}));
                    if (response.status === 403 || err.status === 'blocked') {
                        alert(err.error || 'Bu kullanıcı engellendi veya sizi engelledi. Mesaj iletilemedi.');
                    }
                    else {
                        alert(err.error || 'Mesaj gönderilemedi.');
                    }
                }
            }
            catch (error) {
                console.error('Failed to save DM in background:', error);
            }
        },
        markDMsAsDelivered: async () => {
            try {
                await fetch(`${exports.API_BASE_URL}/dms/delivered`, { method: 'POST', credentials: 'include' });
            }
            catch (error) {
                console.error('Failed to mark DMs as delivered:', error);
            }
        },
        markDMsAsRead: async (otherId) => {
            try {
                await fetch(`${exports.API_BASE_URL}/dms/read`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ otherId }),
                    credentials: 'include',
                });
            }
            catch (error) {
                console.error('Failed to mark DMs as read:', error);
            }
        },
        pingUser: async () => {
            try {
                await fetch(`${exports.API_BASE_URL}/users/ping`, { method: 'POST', credentials: 'include' });
            }
            catch (error) {
                // Silently fail
            }
        },
        friends: [],
        directMessages: [],
        selectedDMUserId: null,
        setSelectedDMUser: (userId) => set({
            selectedDMUserId: userId,
            // When entering DM mode, clear the server selection. When leaving DM mode, keep last server.
            selectedServerId: userId ? null : get().selectedServerId,
            // Always clear any pending reply when switching conversations
            activeReply: null,
            mobileSidebarOpen: false,
            voiceViewVisible: false,
        }),
        // Block Management
        blockedUsers: [],
        blockedUserIds: [],
        blockedByOtherIds: [],
        fetchBlockedUsers: async () => {
            try {
                const [resUsers, resIds] = await Promise.all([
                    fetch(`${exports.API_BASE_URL}/friends/blocked`, { credentials: 'include' }),
                    fetch(`${exports.API_BASE_URL}/friends/blocked-ids`, { credentials: 'include' })
                ]);
                if (resUsers.ok) {
                    const users = await resUsers.json();
                    set({ blockedUsers: users || [] });
                }
                if (resIds.ok) {
                    const ids = await resIds.json();
                    set({
                        blockedUserIds: (ids.blocked_by_me || []).map(String),
                        blockedByOtherIds: (ids.blocked_me || []).map(String)
                    });
                }
            }
            catch (error) {
                console.error('Failed to fetch blocked users:', error);
            }
        },
        blockUser: async (userId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/friends/block`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ blockedUserId: userId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await Promise.all([
                        get().fetchFriends(),
                        get().fetchBlockedUsers()
                    ]);
                }
                else {
                    const err = await response.json();
                    throw new Error(err.error || 'Kullanıcı engellenemedi');
                }
            }
            catch (error) {
                console.error('Failed to block user:', error);
                throw error;
            }
        },
        unblockUser: async (userId) => {
            try {
                const response = await fetch(`${exports.API_BASE_URL}/friends/unblock`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ blockedUserId: userId }),
                    credentials: 'include',
                });
                if (response.ok) {
                    await Promise.all([
                        get().fetchFriends(),
                        get().fetchBlockedUsers()
                    ]);
                }
                else {
                    const err = await response.json();
                    throw new Error(err.error || 'Engel kaldırılamadı');
                }
            }
            catch (error) {
                console.error('Failed to unblock user:', error);
                throw error;
            }
        },
        // Server Connection Status Implementation
        isServerDisconnected: false,
        reconnectAttempt: 0,
        isReconnecting: false,
        reconnectFailed: false,
        setServerDisconnected: (disconnected) => {
            if (disconnected) {
                if (!get().isServerDisconnected) {
                    get().triggerReconnect();
                }
            }
            else {
                set({ isServerDisconnected: false, reconnectAttempt: 0, isReconnecting: false, reconnectFailed: false });
            }
        },
        triggerReconnect: async () => {
            set({ isServerDisconnected: true, isReconnecting: true, reconnectFailed: false });
            for (let attempt = 1; attempt <= 3; attempt++) {
                set({ reconnectAttempt: attempt });
                try {
                    const res = await fetch(`${exports.API_BASE_URL}/users/ping`, { method: 'POST', credentials: 'include' });
                    if (res.ok || res.status < 500) {
                        // Connection restored!
                        set({ isServerDisconnected: false, reconnectAttempt: 0, isReconnecting: false, reconnectFailed: false });
                        get().checkAuth().catch(console.warn);
                        return true;
                    }
                }
                catch (err) {
                    console.warn(`[Connection] Reconnect attempt ${attempt}/3 failed`, err);
                }
                // Wait 3.5 seconds before next attempt
                if (attempt < 3) {
                    await new Promise((resolve) => setTimeout(resolve, 3500));
                }
            }
            // 3 attempts completed and failed -> stop trying automatically, show 'Tekrar Dene' button
            set({ isReconnecting: false, reconnectFailed: true });
            return false;
        },
    };
});
